import { Resource } from 'src_common/common/mobx-utils/Resource';
import { computed, action, observable, makeAutoObservable } from 'mobx';
import { TrpcClient } from './TrpcClient';
import { bettingDataTypes } from 'src_server/types';

export class BettingConfigsState {
    public readonly bettingConfigs: Resource<bettingDataTypes.ResponseType>;

    @observable private processOngoing = { betReferralEnabled: false, betBuilderReferralEnabled: false };

    public constructor(private readonly trpcClient: TrpcClient) {
        this.bettingConfigs = new Resource(async (): Promise<bettingDataTypes.ResponseType> => {
            const response = await this.trpcClient.client.bettingData.getBettingConfigs.query();
            return response;
        });
        makeAutoObservable(this);
    }

    @computed private get bettingConfigsResponse(): bettingDataTypes.SuccessResponseDataType | null {
        const response = this.bettingConfigs.get();
        if (response.type === 'ready' && response.value.responseStatus === 'success') {
            return response.value.data;
        }
        return null;
    }

    @computed public get isBetReferralEnabled(): boolean {
        if (this.bettingConfigsResponse === null) {
            return false;
        }
        if (this.processOngoing.betReferralEnabled) {
            return !this.bettingConfigsResponse.betReferralEnabled;
        }
        return this.bettingConfigsResponse.betReferralEnabled;
    }

    @computed public get isBetBuilderReferralEnabled(): boolean {
        if (this.bettingConfigsResponse === null) {
            return false;
        }

        const betBuilderReferralEnabled = this.bettingConfigsResponse.betBuilderReferralEnabled ?? false;

        if (this.processOngoing.betBuilderReferralEnabled) {
            return !betBuilderReferralEnabled;
        }

        return betBuilderReferralEnabled;
    }

    @action public handleSetBetReferral = async (): Promise<void> => {
        if (this.processOngoing.betReferralEnabled) {
            return;
        }
        const currentValue = this.isBetReferralEnabled;
        this.processOngoing.betReferralEnabled = true;

        try {
            await this.trpcClient.client.bettingData.patchBetReferralData.mutate({
                betReferralEnabled: !currentValue,
            });
            await this.bettingConfigs.refresh();
        } catch (error) {
            console.error(error);
        }

        this.processOngoing.betReferralEnabled = false;
    };

    @action public handleSetBetBuilderReferral = async (): Promise<void> => {
        if (this.processOngoing.betBuilderReferralEnabled) {
            return;
        }
        const currentValue = this.isBetBuilderReferralEnabled;
        this.processOngoing.betBuilderReferralEnabled = true;

        try {
            await this.trpcClient.client.bettingData.patchBetReferralData.mutate({
                betBuilderReferralEnabled: !currentValue,
            });
            await this.bettingConfigs.refresh();
        } catch (error) {
            console.error(error);
        }

        this.processOngoing.betBuilderReferralEnabled = false;
    };
}
